"use client";

import React, { useState, useEffect } from "react";
import { Menu } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
const hiddenJobPageLabels = ["Client Financial Administration Lead", "Financial Program and Reporting Lead", "Financial Reporting and Program Support Lead", "Microsoft SharePoint and Systems Developer", "General Expression of Interest"];
const hiddenJobPagePaths = ["/ClientFinancialAdministrationLead", "/ClientFinancialAdministrationLeadPage", "/client-financial-administration-lead/", "/FinancialProgramAndReportingLead", "/FinancialProgramAndReportingLeadPage", "/financial-program-and-reporting-lead/", "/FinancialReportingAndProgramSupportLead", "/FinancialReportingAndProgramSupportLeadPage", "/financial-reporting-and-program-support-lead/", "/MicrosoftSharePointAndSystemsDeveloper", "/MicrosoftSharePointAndSystemsDeveloperPage", "/microsoft-sharepoint-and-systems-developer/", "/GeneralExpressionOfInterest", "/GeneralExpressionOfInterestPage", "/general-expression-of-interest/"];
const linkMap: Record<string, string> = {
  "/": "/",
  "#": "/",
  "/HomePage": "/",
  "/home": "/",
  "/home/": "/",
  "/AboutRiverBendPage": "/about-riverbend/",
  "/AboutRiverBend": "/about-riverbend/",
  "/about-riverbend": "/about-riverbend/",
  "/about-riverbend/": "/about-riverbend/",
  "/ServicesPage": "/services/",
  "/Services": "/services/",
  "/services": "/services/",
  "/services/": "/services/",
  "/OurTeamPage": "/our-team/",
  "/OurTeam": "/our-team/",
  "/our-team": "/our-team/",
  "/our-team/": "/our-team/",
  "/InsightsPage": "/insights/",
  "/Insights": "/insights/",
  "/insights": "/insights/",
  "/insights/": "/insights/",
  "/ContactPage": "/contact/",
  "/Contact": "/contact/",
  "/contact": "/contact/",
  "/contact/": "/contact/",
  "/OurCommitmentToEmployeesPage": "/our-commitment-to-employees/",
  "/OurCommitmentToEmployees": "/our-commitment-to-employees/",
  "/our-commitment-to-employees": "/our-commitment-to-employees/",
  "/our-commitment-to-employees/": "/our-commitment-to-employees/",
  "/TeamAndCareersPage": "/career-opportunities/",
  "/CareerOpportunitiesPage": "/career-opportunities/",
  "/CareerOpportunities": "/career-opportunities/",
  "/jobs": "/career-opportunities/",
  "/jobs/": "/career-opportunities/",
  "/career-opportunities": "/career-opportunities/",
  "/career-opportunities/": "/career-opportunities/",
  "/ClientFinancialAdministrationLead": "/client-financial-administration-lead/",
  "/ClientFinancialAdministrationLeadPage": "/client-financial-administration-lead/",
  "/client-financial-administration-lead": "/client-financial-administration-lead/",
  "/client-financial-administration-lead/": "/client-financial-administration-lead/",
  "/FinancialProgramAndReportingLead": "/financial-program-and-reporting-lead/",
  "/FinancialProgramAndReportingLeadPage": "/financial-program-and-reporting-lead/",
  "/financial-program-and-reporting-lead": "/financial-program-and-reporting-lead/",
  "/financial-program-and-reporting-lead/": "/financial-program-and-reporting-lead/",
  "/FinancialReportingAndProgramSupportLead": "/financial-reporting-and-program-support-lead/",
  "/FinancialReportingAndProgramSupportLeadPage": "/financial-reporting-and-program-support-lead/",
  "/financial-reporting-and-program-support-lead": "/financial-reporting-and-program-support-lead/",
  "/financial-reporting-and-program-support-lead/": "/financial-reporting-and-program-support-lead/",
  "/MicrosoftSharePointAndSystemsDeveloper": "/microsoft-sharepoint-and-systems-developer/",
  "/MicrosoftSharePointAndSystemsDeveloperPage": "/microsoft-sharepoint-and-systems-developer/",
  "/microsoft-sharepoint-and-systems-developer": "/microsoft-sharepoint-and-systems-developer/",
  "/microsoft-sharepoint-and-systems-developer/": "/microsoft-sharepoint-and-systems-developer/",
  "/GeneralExpressionOfInterest": "/general-expression-of-interest/",
  "/GeneralExpressionOfInterestPage": "/general-expression-of-interest/",
  "/general-expression-of-interest": "/general-expression-of-interest/",
  "/general-expression-of-interest/": "/general-expression-of-interest/"
};
function normalizeHref(href?: string) {
  const rawHref = (href || "").trim();
  if (!rawHref || rawHref === "#") {
    return "/";
  }
  if (rawHref.startsWith("mailto:") || rawHref.startsWith("tel:") || rawHref.startsWith("http://") || rawHref.startsWith("https://")) {
    return rawHref;
  }
  const [pathPart, hashPart] = rawHref.split("#");
  const path = pathPart || "/";
  const hash = hashPart ? `#${hashPart}` : "";
  const normalizedPath = linkMap[path] || path;
  return `${normalizedPath}${hash}`;
}
function shouldShowMenuItem(item: {
  label?: string;
  href?: string;
}) {
  const label = (item.label || "").trim();
  const href = normalizeHref(item.href);
  if (hiddenJobPageLabels.includes(label)) {
    return false;
  }
  if (hiddenJobPagePaths.includes(href)) {
    return false;
  }
  return true;
}
function HeaderMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="205165" className="hidden md:flex gap-6">
        {[1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="h-5 w-20 bg-muted animate-pulse rounded-sm" />)}
      </div>;
  }
  const visibleMenuItems = menuItems.filter(shouldShowMenuItem);
  return <NavigationMenu viewport={false} className="hidden md:flex">
      <NavigationMenuList className="gap-7 lg:gap-8">
        {visibleMenuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={normalizeHref(item.href)} className="text-sm font-medium font-default uppercase tracking-wider text-primary hover:text-secondary transition-colors duration-200 whitespace-nowrap">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onNavigate
}: {
  onNavigate: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-4 mt-8">
        {[1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="h-8 w-3/4 bg-muted animate-pulse rounded-sm" />)}
      </div>;
  }
  const visibleMenuItems = menuItems.filter(shouldShowMenuItem);
  return <nav className="flex flex-col gap-6 mt-12">
      {visibleMenuItems.map((item, i) => <div key={item.id} data-index={i}>
          <Link to={normalizeHref(item.href)} className="text-lg font-medium font-default uppercase tracking-wider text-primary hover:text-secondary transition-colors block" onClick={onNavigate}>
            {item.label}
          </Link>
        </div>)}
    </nav>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (!anchor) return;
      const href = anchor.getAttribute("href");
      if (!href) return;
      if (href === "/#" || href === "#") {
        e.preventDefault();
        window.scrollTo({
          top: 0,
          behavior: "smooth"
        });
        setIsMobileMenuOpen(false);
        return;
      }
      let hash = "";
      if (href.startsWith("#")) {
        hash = href;
      } else if (href.startsWith("/") && href.includes("#")) {
        const [path, hashPart] = href.split("#");
        if (hashPart === "") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          setIsMobileMenuOpen(false);
          return;
        }
        if (path === "/" || path === window.location.pathname) {
          hash = "#" + hashPart;
        }
      }
      if (hash && hash !== "#") {
        const element = document.querySelector(hash);
        if (element) {
          e.preventDefault();
          element.scrollIntoView({
            behavior: "smooth",
            block: "start"
          });
          setIsMobileMenuOpen(false);
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section id="header" className={`sticky top-0 z-50 w-full transition-all duration-300 ease-in-out ${isScrolled ? "bg-background/80 backdrop-blur-md border-b border-border shadow-md py-3" : "bg-background py-4 border-b border-transparent"}`}>
      <div className="max-w-7xl mx-auto px-6 lg:px-8 flex items-center justify-between gap-6">
        <Link to="/" className="flex items-center gap-2 z-50 relative shrink-0">
          <img src="https://stable-woodcock.10web.cloud/wp-content/uploads/2026/04/image_q6l7uk6d.png" alt="RiverBend Accounting & Management Services" className="h-12 md:h-16 w-auto object-contain" />
        </Link>

        <div className="hidden md:flex items-center justify-end flex-1">
          <MenuProvider menu_id="105">
            <HeaderMenuConsumer />
          </MenuProvider>
        </div>

        <div className="md:hidden flex items-center">
          <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="text-foreground hover:bg-accent/20">
                <Menu className="size-6" />
                <span className="sr-only">Toggle menu</span>
              </Button>
            </SheetTrigger>

            <SheetContent side="right" className="w-[300px] sm:w-[400px] bg-background border-l border-border p-6">
              <SheetTitle className="sr-only">Navigation Menu</SheetTitle>

              <div className="flex items-center justify-between mb-8">
                <Link to="/" onClick={() => setIsMobileMenuOpen(false)}>
                  <img src="https://stable-woodcock.10web.cloud/wp-content/uploads/2026/04/Riverbend_Logo-scaled.png" alt="RiverBend Accounting & Management Services" className="h-12 w-auto object-contain" />
                </Link>
              </div>

              <MenuProvider menu_id="105">
                <MobileMenuConsumer onNavigate={() => setIsMobileMenuOpen(false)} />
              </MenuProvider>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </section>;
}